/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ButtonComponent,
  GridCardComponent,
  IconName,
  InputComponent,
  EmptyComponent,
} from 'nebula-ui-kit';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductsService } from '../../../../data-access/src/lib/products.service';
import { Module, ModuleItem, OpcionProducto } from '../../../../domain/src/lib/products.model';
import { PATH_ROUTES } from '../../../../const/src/lib/products.constant';
import { toSignal } from '@angular/core/rxjs-interop';
import { StateService } from '@nebula-shared/master';
import {
  ConfiguracionRolService,
  EstadoDeTrabajoService,
  ThemeService,
  TranslatePipe,
  TranslationService,
  UiProfileService,
  guardarContextoDeProducto,
  leerDeLaPestana,
} from '@nebula-shared/data-access';

/**
 * Diseño con que el perfil 3.0 presenta los productos. Lo elige la compañia en el campo
 * `selectorProducto` del parametro `APARIENCIA` (orden de Carlos del 30-oct-2026).
 */
export type DisenoSelector = 'ACTUAL' | 'LISTA_DETALLE' | 'ICONO_CENTRADO' | 'MOSAICO';

/** Diseños que el selector sabe pintar; cualquier otro valor deja el actual. */
const DISENOS_SELECTOR: readonly DisenoSelector[] = [
  'ACTUAL',
  'LISTA_DETALLE',
  'ICONO_CENTRADO',
  'MOSAICO',
];

/** Tono de una pieza del mosaico, siempre de la paleta del primario del perfil 3.0. */
export type TonoMosaico = 'primario' | 'oscuro' | 'claro' | 'blanco';

/** Pieza del mosaico: el producto, cuantas columnas y filas ocupa y su tono. */
export interface PiezaMosaico {
  modulo: ModuleItem;
  columnas: number;
  filas: number;
  tono: TonoMosaico;
}

/**
 * Tamaño y tono de cada posicion del mosaico, en el orden del menu: la primera pieza ocupa
 * tres columnas y dos filas, las dos siguientes tres columnas y el resto dos. Pasadas las nueve
 * posiciones se repiten las piezas pequeñas.
 */
const PATRON_MOSAICO: readonly Omit<PiezaMosaico, 'modulo'>[] = [
  { columnas: 3, filas: 2, tono: 'primario' },
  { columnas: 3, filas: 1, tono: 'oscuro' },
  { columnas: 3, filas: 1, tono: 'claro' },
  { columnas: 2, filas: 1, tono: 'blanco' },
  { columnas: 2, filas: 1, tono: 'blanco' },
  { columnas: 2, filas: 1, tono: 'primario' },
  { columnas: 2, filas: 1, tono: 'claro' },
  { columnas: 2, filas: 1, tono: 'oscuro' },
  { columnas: 2, filas: 1, tono: 'blanco' },
];

/** Posicion del patron donde empiezan las piezas pequeñas que se repiten. */
const INICIO_PIEZAS_PEQUENAS = 3;

/** Producto que ocupa la pieza principal del mosaico (orden de Carlos del 30-oct-2026). */
const PRODUCTO_PRINCIPAL_MOSAICO = 'NEBULA_ACCOUNTING';

/** Producto que va siempre en una pieza pequeña del mosaico (orden de Carlos del 30-oct-2026). */
const PRODUCTO_PEQUENO_MOSAICO = 'NEBULA_CONFIG';

/**
 * Accesos directos que muestra el detalle de LISTA_DETALLE: uno grande y hasta ocho pequeños,
 * tomados al azar del menu del producto (orden de Carlos del 30-oct-2026).
 */
const MAXIMO_ACCESOS = 9;

/**
 * Accesos directos que aparecen al pasar el foco por una tarjeta de ICONO_CENTRADO (mockup
 * «Selector Producto Iconos», 30-oct-2026, muestra tres).
 */
export const MAXIMO_ACCESOS_ICONO = 3;

/** Acceso directo del detalle: la opcion del menu y si ocupa la pieza grande. */
export interface AccesoDirecto {
  opcion: OpcionProducto;
  grande: boolean;
}

@Component({
  selector: 'app-products',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    MatTooltipModule,
    ButtonComponent,
    GridCardComponent,
    InputComponent,
    EmptyComponent,
    TranslatePipe,
  ],
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss'],
})
export class ProductsComponent implements OnInit {
  readonly productsService = inject(ProductsService);
  readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);
  private readonly stateService = inject(StateService);
  private readonly translation = inject(TranslationService);
  private readonly configuracionRol = inject(ConfiguracionRolService);

  private translateLiteral(literal: string): string {
    if (!literal) return literal;
    const value = this.translation.translate(literal);
    return value && value !== literal ? value : literal;
  }
  private readonly tableId = 'products_list';
  public username = '';

  private readonly uiProfile = inject(UiProfileService);
  private readonly estadoDeTrabajo = inject(EstadoDeTrabajoService);
  private readonly tema = inject(ThemeService);

  /** Si la pantalla se ve oscura; lo decide el dueño del tema, no esta pantalla. */
  readonly isDarkMode = this.tema.oscuro;

  /** Si la empresa pidio el perfil 3.0; se resuelve una vez, al construir. */
  readonly perfil30 = this.uiProfile.esPerfil30();

  /** Nombre de la empresa sobre la que se esta trabajando. */
  public empresa = '';

  /** Cuenta de modulos, entera o filtrada, tal como se rotula al lado del buscador. */
  readonly conteo = computed<string>(() => {
    this.translation.lang();
    const total = this.modules().length;
    const visibles = this.filteredModules().length;
    return visibles === total
      ? this.translation.translate('SELECTOR.MODULE_COUNT_ALL', { total })
      : this.translation.translate('SELECTOR.MODULE_COUNT_FILTERED', { visibles, total });
  });

  /**
   * Diseño del selector que pidio la compañia. Sin parametro, o con un valor que no se conoce,
   * vale el actual.
   */
  readonly disenoSelector = computed<DisenoSelector>(() => {
    const valor = this.configuracionRol.parametro<{ selectorProducto?: string }>('APARIENCIA')
      ?.selectorProducto as DisenoSelector | undefined;
    return valor && DISENOS_SELECTOR.includes(valor) ? valor : 'ACTUAL';
  });

  /** Orden al azar de las opciones de cada producto; se sortea una vez por producto. */
  private readonly opcionesAlAzar = new Map<string, OpcionProducto[]>();

  /**
   * Accesos directos del producto en detalle: el primero es la pieza grande y los demas pequeños.
   */
  readonly accesosDirectos = computed<AccesoDirecto[]>(() => {
    const detalle = this.productoEnDetalle();
    if (!detalle) return [];
    return this.opcionesSorteadas(detalle)
      .slice(0, MAXIMO_ACCESOS)
      .map((opcion, i) => ({ opcion, grande: i === 0 }));
  });

  /**
   * Accesos directos de una tarjeta de ICONO_CENTRADO: los primeros del mismo sorteo del
   * producto, de modo que no cambian al volver a pintar.
   *
   * @param modulo producto de la tarjeta.
   * @param maximo cuantos accesos muestra la tarjeta.
   * @returns hasta `maximo` opciones del menu del producto.
   */
  accesosDeTarjeta(modulo: ModuleItem, maximo: number): OpcionProducto[] {
    return this.opcionesSorteadas(modulo).slice(0, maximo);
  }

  /** Accesos que muestra cada tarjeta de ICONO_CENTRADO al pasar el foco. */
  readonly maximoAccesosIcono = MAXIMO_ACCESOS_ICONO;

  /**
   * Opciones del producto en orden al azar; se sortean una vez por producto y se guardan.
   *
   * @param modulo producto.
   * @returns las opciones barajadas, o una lista vacia si el producto no tiene.
   */
  private opcionesSorteadas(modulo: ModuleItem): OpcionProducto[] {
    if (!modulo.opciones?.length) return [];
    const clave = modulo.id ?? modulo.title;
    let sorteadas = this.opcionesAlAzar.get(clave);
    if (!sorteadas) {
      sorteadas = this.alAzar(modulo.opciones);
      this.opcionesAlAzar.set(clave, sorteadas);
    }
    return sorteadas;
  }

  /** Producto que el usuario marco en la lista del diseño de lista y detalle. */
  readonly productoMarcado = signal<string | undefined>(undefined);

  /** Producto que muestra el detalle: el marcado si sigue visible, si no el primero visible. */
  readonly productoEnDetalle = computed<ModuleItem | undefined>(() => {
    const visibles = this.filteredModules();
    return visibles.find((m) => m.id === this.productoMarcado()) ?? visibles[0];
  });

  /**
   * Piezas del diseño de mosaico; todas llevan su descripcion. Mientras se busca, todas son del
   * mismo tamaño, para que el resultado se lea como una lista pareja.
   */
  readonly mosaico = computed<PiezaMosaico[]>(() => {
    const buscando = this.searchTerm().trim().length > 0;
    const repetidas = PATRON_MOSAICO.length - INICIO_PIEZAS_PEQUENAS;
    return this.ordenMosaico(this.filteredModules()).map((modulo, i) => {
      const patron =
        i < PATRON_MOSAICO.length
          ? PATRON_MOSAICO[i]
          : PATRON_MOSAICO[INICIO_PIEZAS_PEQUENAS + ((i - INICIO_PIEZAS_PEQUENAS) % repetidas)];
      const columnas = buscando ? 2 : patron.columnas;
      const filas = buscando ? 1 : patron.filas;
      return {
        modulo,
        columnas,
        filas,
        tono: patron.tono,
      };
    });
  });

  /**
   * Orden de los productos en el mosaico: el principal va primero, en la pieza grande, y el
   * pequeño pasa a la primera pieza pequeña; el resto conserva el orden del menu.
   *
   * @param visibles productos visibles, en el orden del menu.
   * @returns los mismos productos en el orden del mosaico.
   */
  private ordenMosaico(visibles: ModuleItem[]): ModuleItem[] {
    const codigos = new Map(this.modulesRaw().map((m: Module) => [m.id, m.code]));
    const principal = visibles.filter((m) => codigos.get(m.id) === PRODUCTO_PRINCIPAL_MOSAICO);
    const pequeno = visibles.filter((m) => codigos.get(m.id) === PRODUCTO_PEQUENO_MOSAICO);
    const resto = visibles.filter((m) => !principal.includes(m) && !pequeno.includes(m));
    const ordenados = [...principal, ...resto];
    ordenados.splice(Math.min(INICIO_PIEZAS_PEQUENAS, ordenados.length), 0, ...pequeno);
    return ordenados;
  }

  searchTerm = signal('');
  showBackButton = signal(false);

  modulesRaw = toSignal(this.productsService.getModules(), {
    initialValue: [],
  });

  modules = computed<ModuleItem[]>(() => {
    const data = this.modulesRaw();
    this.translation.lang();

    return data.map((item: Module) => {
      const rawRoute = `${PATH_ROUTES.LAYOUT + item.route}`;
      return {
        id: item.id,
        title: this.translateLiteral(item.name),
        subtitle: this.translateLiteral(item.description),
        icon: item.icon as IconName,
        route: rawRoute,
        opciones: (item.opciones ?? []).map((opcion) => ({
          ...opcion,
          nombre: this.translateLiteral(opcion.nombre),
          ruta: this.rutaDeOpcion(item.route ?? '', opcion.ruta),
        })),
      };
    });
  });

  filteredModules = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    return this.modules().filter(
      (m) =>
        m.title.toLowerCase().includes(term) || (m.subtitle?.toLowerCase().includes(term) ?? false),
    );
  });

  ngOnInit(): void {
    const userDataString = leerDeLaPestana('user_data');
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      this.username = userData.username || '';
      this.empresa = userData.companyName || userData.empresa || '';
    } else {
      this.username = '';
      this.empresa = '';
    }

    const autoRedirect = this.route.snapshot.queryParams['autoRedirect'];
    this.showBackButton.set(Boolean(autoRedirect));

    this.loadSavedState();
  }

  private loadSavedState(): void {
    const savedState = this.stateService.getState(this.tableId);
    if (savedState && savedState.searchTerm) {
      this.searchTerm.set(savedState.searchTerm);
    }
  }

  private saveCurrentState(): void {
    const state = {
      searchTerm: this.searchTerm(),
    };
    this.stateService.saveState(this.tableId, state);
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.saveCurrentState();
  }

  onBack(): void {
    const customerId = this.route.snapshot.queryParams['customerId'];
    this.router.navigate([`${PATH_ROUTES.LAYOUT}/company`], {
      queryParams: customerId ? { customerId } : undefined,
    });
  }

  /**
   * Anota el producto elegido y entra a el DESDE CERO: sin pestañas, borradores ni marcas que
   * hayan quedado de antes (regla de Carlos, 29-sep-2026). La recarga de la entrada manda sobre
   * la navegacion que la tarjeta del kit hace por su cuenta.
   */
  selectProduct(event: { item: any }, destino?: string) {
    const product = event.item || event;

    let cleanRoute = product.route || '';
    cleanRoute = cleanRoute.replace(/^\//, '');

    if (cleanRoute.startsWith('layout/')) {
      cleanRoute = cleanRoute.substring(7);
    }

    guardarContextoDeProducto('selectedProductId', String(product.id));
    // El codigo viaja con el producto: los parametros de configuracion se piden por el (HT_ARQ_012).
    this.configuracionRol.fijarProductoActivo(product.code);
    guardarContextoDeProducto('selectedRoute', cleanRoute);
    const ruta = destino || product.route;
    if (ruta) this.estadoDeTrabajo.entrarDesdeCero(ruta);
  }

  /**
   * Entra al modulo elegido.
   *
   * El perfil vigente delega la navegacion en la tarjeta del kit, que la resuelve
   * por su `route`. Aqui la tarjeta es propia: anotar el producto y entrar desde cero
   * son el mismo gesto, y los dos los hace `selectProduct`.
   */
  entrarAlModulo(modulo: ModuleItem): void {
    this.selectProduct({ item: modulo });
  }

  /**
   * Entra al producto directo en una de sus opciones, con el mismo contexto que deja Entrar.
   *
   * @param modulo producto de la opcion.
   * @param opcion opcion del menu a la que se entra.
   */
  entrarAOpcion(modulo: ModuleItem, opcion: OpcionProducto): void {
    this.selectProduct({ item: modulo }, opcion.ruta);
  }

  /**
   * Cuenta de procesos (opciones del menu) de un producto, tal como se rotula en la lista.
   *
   * @param modulo producto.
   * @returns el rotulo traducido.
   */
  conteoDeProcesos(modulo: ModuleItem): string {
    const total = modulo.opciones?.length ?? 0;
    return this.translation.translate(
      total === 1 ? 'SELECTOR.PROCESS_COUNT_ONE' : 'SELECTOR.PROCESS_COUNT',
      { total },
    );
  }

  /**
   * Ruta completa de una opcion: `/layout/<producto>/<opcion>`. Si la ruta del menu ya trae el
   * segmento del producto, no se repite.
   *
   * @param rutaProducto ruta del producto en el menu (p. ej. `/maestros`).
   * @param rutaOpcion ruta de la opcion en el menu.
   * @returns la ruta con la que se entra a la opcion.
   */
  private rutaDeOpcion(rutaProducto: string, rutaOpcion: string): string {
    const opcion = '/' + rutaOpcion.replace(/^\/+/, '');
    if (opcion.startsWith(PATH_ROUTES.LAYOUT + '/')) return opcion;
    const producto = '/' + rutaProducto.replace(/^\/+|\/+$/g, '');
    return opcion.startsWith(producto + '/')
      ? PATH_ROUTES.LAYOUT + opcion
      : PATH_ROUTES.LAYOUT + producto + opcion;
  }

  /**
   * Copia de las opciones en orden al azar (Fisher-Yates).
   *
   * @param opciones opciones del producto.
   * @returns las mismas opciones, barajadas.
   */
  private alAzar(opciones: OpcionProducto[]): OpcionProducto[] {
    const copia = [...opciones];
    for (let i = copia.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copia[i], copia[j]] = [copia[j], copia[i]];
    }
    return copia;
  }

  /**
   * Marca un producto en la lista del diseño de lista y detalle; entrar es otro gesto.
   *
   * @param modulo producto marcado.
   */
  marcarProducto(modulo: ModuleItem): void {
    this.productoMarcado.set(modulo.id);
  }

  clearSearch(): void {
    this.searchTerm.set('');
    this.saveCurrentState();
  }
}
